<template>
  <b-modal
    :visible="Boolean(file)"
    size="md"
    modal-class="catalog-image-crop-dialog"
    centered
    hide-header
    hide-footer
    @hidden="$emit('cancel')"
  >
    <p class="catalog-image-crop__hint">Arraste a foto dentro da moldura para ajustar o enquadramento.</p>
    <div class="catalog-image-crop__workspace">
      <div class="catalog-image-crop__stage" :style="{ aspectRatio: aspectRatio }">
        <vue-cropper
          v-if="source"
          ref="cropper"
          :src="source"
          :container-style="{ width: '100%', height: '100%', aspectRatio: aspectRatio }"
          :img-style="{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }"
          :aspect-ratio="aspectRatio"
          :view-mode="1"
          :auto-crop-area="0.84"
          :movable="true"
          :zoomable="true"
          :background="false"
          :guides="false"
          :center="false"
          :crop-box-movable="false"
          :crop-box-resizable="false"
          :toggle-drag-mode-on-dblclick="false"
          drag-mode="move"
          :crop="updateQuality"
          :ready="onReady"
          :zoom="onZoom"
        />
      </div>
      <div class="catalog-image-crop__zoom-rail" aria-label="Controles de zoom">
        <button type="button" aria-label="Aumentar zoom" :disabled="!baseZoom" @click="stepZoom(0.1)">+</button>
        <button type="button" aria-label="Diminuir zoom" :disabled="!baseZoom" @click="stepZoom(-0.1)">−</button>
      </div>
    </div>
    <div class="catalog-image-crop__zoom mt-3">
      <input type="range" :min="minZoomLevel" max="3" step="0.01" :value="zoomLevel" :disabled="!baseZoom" aria-label="Zoom da foto" @input="setZoom($event.target.value)" />
      <button type="button" class="catalog-image-crop__reset" :disabled="!baseZoom" @click="reset">Restaurar</button>
    </div>
    <div class="catalog-image-crop__info mt-3">
      <p class="catalog-image-crop__info-title">Resolução da imagem</p>

      <p v-if="sourceDimensions.width && sourceDimensions.height">
        Original: {{ sourceDimensions.width }} × {{ sourceDimensions.height }} px
      </p>

      <p v-else>Carregando resolução...</p>

      <p>Área selecionada: {{ quality.width }} × {{ quality.height }} px</p>
      <p>Mínimo para o recorte: {{ minCropWidth }} × {{ minCropHeight }} px</p>

      <p
        v-if="qualityFeedback"
        class="catalog-image-crop__feedback"
        :class="quality.valid ? 'text-success' : 'text-warning'"
        role="status"
      >
        {{ qualityFeedback }}
      </p>
    </div>

    <div class="catalog-image-crop__actions">
      <b-button
        variant="light"
        @click="$emit('cancel')"
      >
        Cancelar
      </b-button>

      <b-button
        variant="primary"
        :disabled="!quality.valid || applying"
        @click="apply"
      >
        Usar esta foto
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import VueCropper from "vue-cropperjs";
import "cropperjs/dist/cropper.css";

export default {
  name: "CatalogImageCropModal",
  components: { VueCropper },
  props: {
    file: { type: File, default: null },
    aspectRatio: { type: Number, default: 1 },
    minCropWidth: { type: Number, default: 800 },
    minCropHeight: { type: Number, default: 800 },
    maxPublishedWidth: { type: Number, default: 1200 },
    maxPublishedHeight: { type: Number, default: 1200 },
    outputQuality: { type: Number, default: 0.85 },
    destinationLabel: { type: String, default: "catálogo" },
    emitCroppedFile: { type: Boolean, default: false },
  },
  data() {
    return {
      source: "",
      applying: false,
      baseZoom: 0,
      zoomLevel: 1,
      minZoomLevel: 1,
      initialZoomLevel: 1,
      sourceDimensions: { width: 0, height: 0 },
      quality: { width: 0, height: 0, valid: false, label: "insuficiente" },
    };
  },
  computed: {
    qualityFeedback() {
      if (!this.sourceDimensions.width || !this.sourceDimensions.height) return "";
      if (this.quality.valid) return `✓ Imagem adequada para o ${this.destinationLabel}.`;
      if (this.sourceDimensions.width < this.minCropWidth || this.sourceDimensions.height < this.minCropHeight) {
        return "⚠ A imagem possui resolução baixa para o recorte mínimo.";
      }
      return "⚠ A área selecionada está abaixo do mínimo. Reduza o zoom.";
    },
  },
  watch: {
    file(file) {
      if (this.source) URL.revokeObjectURL(this.source);
      this.source = file ? URL.createObjectURL(file) : "";
      this.quality = { width: 0, height: 0, valid: false, label: "insuficiente" };
      this.applying = false;
      this.baseZoom = 0;
      this.zoomLevel = 1;
      this.minZoomLevel = 1;
      this.initialZoomLevel = 1;
      this.sourceDimensions = { width: 0, height: 0 };
    },
  },
  beforeDestroy() {
    if (this.source) URL.revokeObjectURL(this.source);
  },
  methods: {
    onReady() {
      this.$nextTick(() => {
        const cropper = this.$refs.cropper;
        if (!cropper) return;
        const canvas = cropper.getCanvasData();
        const image = cropper.getImageData();
        const cropBox = cropper.getCropBoxData();
        if (!image.naturalWidth || !image.naturalHeight) return;
        this.sourceDimensions = { width: image.naturalWidth, height: image.naturalHeight };
        this.baseZoom = canvas.width / image.naturalWidth;
        const smallestCoverRatio = Math.max(
          cropBox.width / image.naturalWidth,
          cropBox.height / image.naturalHeight
        );
        this.minZoomLevel = Math.min(1, smallestCoverRatio / this.baseZoom);
        const availableZoom = Math.min(
          image.naturalWidth / this.minCropWidth,
          image.naturalHeight / this.minCropHeight
        );
        this.initialZoomLevel = Math.min(3, this.minZoomLevel * Math.max(1, Math.min(1.1, availableZoom)));
        this.zoomLevel = this.initialZoomLevel;
        cropper.zoomTo(this.baseZoom * this.initialZoomLevel);
        this.updateQuality();
      });
    },
    onZoom(event) {
      if (this.baseZoom && event && event.detail) {
        this.zoomLevel = Math.max(this.minZoomLevel, Math.min(3, event.detail.ratio / this.baseZoom));
      }
    },
    updateQuality() {
      const cropper = this.$refs.cropper;
      if (!cropper) return;
      const data = cropper.getData(true);
      const width = Math.round(data.width || 0);
      const height = Math.round(data.height || 0);
      const valid = width >= this.minCropWidth && height >= this.minCropHeight;
      this.quality = {
        width,
        height,
        valid,
        label: !valid ? "insuficiente" : width >= this.maxPublishedWidth && height >= this.maxPublishedHeight ? "excelente" : "boa",
      };
    },
    setZoom(value) {
      if (!this.$refs.cropper || !this.baseZoom) return;
      this.zoomLevel = Math.max(this.minZoomLevel, Math.min(3, Number(value)));
      this.$refs.cropper.zoomTo(this.baseZoom * this.zoomLevel);
    },
    stepZoom(delta) {
      this.setZoom(this.zoomLevel + delta);
    },
    reset() {
      if (!this.$refs.cropper || !this.baseZoom) return;
      this.$refs.cropper.reset();
      this.setZoom(this.initialZoomLevel);
    },
    apply() {
      if (!this.quality.valid || !this.$refs.cropper || this.applying) return;
      this.applying = true;
      const file = this.file;
      const data = this.$refs.cropper.getData(true);
      const crop = { x: data.x, y: data.y, width: data.width, height: data.height };
      const canvas = this.$refs.cropper.getCroppedCanvas({
        maxWidth: this.emitCroppedFile ? this.maxPublishedWidth : 600,
        maxHeight: this.emitCroppedFile ? this.maxPublishedHeight : 600,
      });
      if (!canvas) {
        this.applying = false;
        return;
      }
      canvas.toBlob((blob) => {
        this.applying = false;
        if (blob && this.file === file) {
          const result = { file, crop, preview: URL.createObjectURL(blob) };
          if (this.emitCroppedFile) {
            const name = file.name.replace(/\.[^.]+$/, "") + "-recorte.webp";
            result.croppedFile = new File([blob], name, { type: "image/webp" });
          }
          this.$emit("apply", result);
        } else if (!blob && this.file === file) {
          this.$toast.error("Não foi possível preparar a prévia da imagem.");
        }
      }, "image/webp", this.outputQuality);
    },
  },
};
</script>

<style scoped>
.catalog-image-crop__hint {
  color: #c5c9cd;
  margin-bottom: 14px;
}
.catalog-image-crop__workspace {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;
}
.catalog-image-crop__stage {
  width: min(calc(100% - 52px), 380px, 48vh);
  overflow: hidden;
  background: #0d1012;
  border-radius: 10px;
  flex: none;
}
.catalog-image-crop__zoom-rail {
  display: flex;
  flex-direction: column;
  align-self: center;
  background: #353a3e;
  border-radius: 9px;
  overflow: hidden;
}
.catalog-image-crop__zoom-rail button {
  width: 36px;
  height: 38px;
  border: 0;
  background: transparent;
  color: #f5f6f7;
  font-size: 22px;
  line-height: 1;
}
.catalog-image-crop__zoom-rail button + button { border-top: 1px solid #50565a; }
.catalog-image-crop__zoom-rail button:disabled { opacity: 0.4; }
.catalog-image-crop__zoom-rail button:not(:disabled):hover { background: #4b5155; }
.catalog-image-crop__zoom {
  display: flex;
  align-items: center;
  gap: 16px;
}
.catalog-image-crop__zoom input { flex: 1; min-width: 0; accent-color: #ef7064; }
.catalog-image-crop__zoom button {
  border: 0;
  background: transparent;
  color: #e4e8eb;
  font-size: 12px;
  line-height: 1;
}
.catalog-image-crop__zoom button:disabled { opacity: 0.4; }
.catalog-image-crop__zoom .catalog-image-crop__reset { white-space: nowrap; }
.catalog-image-crop__info {
  padding: 12px 14px;
  border: 1px solid #373d42;
  border-radius: 8px;
  background: #202428;
  color: #c5c9cd;
  font-size: 13px;
}
.catalog-image-crop__info p { margin: 0; }
.catalog-image-crop__info p + p { margin-top: 4px; }
.catalog-image-crop__info .catalog-image-crop__info-title {
  color: #f5f6f7;
  font-weight: 600;
  margin-bottom: 8px;
}
.catalog-image-crop__info .catalog-image-crop__feedback {
  border-top: 1px solid #373d42;
  margin-top: 10px;
  padding-top: 10px;
  font-weight: 600;
}

.catalog-image-crop__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.catalog-image-crop__info {
  margin-bottom: 0;
}
</style>

<style>
.catalog-image-crop-dialog .modal-content {
  background: #1a1d20;
  color: #f5f6f7;
  border: 1px solid #373d42;
  border-radius: 12px;
  overflow: hidden;
}
.catalog-image-crop-dialog .modal-header {
  background: #202428;
  border-bottom: 1px solid #373d42;
}
.catalog-image-crop-dialog .modal-header .close {
  color: #f5f6f7;
  opacity: 0.8;
}
.catalog-image-crop-dialog .modal-title {
  color: #f5f6f7;
}
.catalog-image-crop__stage .cropper-line,
.catalog-image-crop__stage .cropper-point {
  display: none;
}
.catalog-image-crop__stage .cropper-view-box {
  outline: 1px solid rgba(255, 255, 255, 0.85);
  outline-offset: -1px;
}
</style>
